import { useCallback, useEffect, useRef } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useKFlags } from '@/hooks/use-k-flags';
import { WORKDAY_INTEGRATION_TYPE, WORKDAY_POSITION_FLAG } from './constants';
import { WORKDAY_POSITION_API, WORKDAY_POSITION_QUERY_KEYS } from './api';
import type { WorkdayPosition } from './types';

interface IntegrationLike {
  type?: string;
  name?: string;
  status?: string;
}

/**
 * Both conditions must hold: the flag is the rollout switch, the connector check
 * is a capability check — these are Workday SOAP operations behind Kombo's
 * passthrough, so any other HRIS would fail at the vendor rather than degrade.
 */
export const useWorkdayPositionGate = (
  integrations: IntegrationLike[] = []
) => {
  const flags = useKFlags();
  const isFlagOn = !!flags[WORKDAY_POSITION_FLAG];

  const workdayIntegration = integrations.find(
    (integration) => integration?.type === WORKDAY_INTEGRATION_TYPE
  );

  return {
    isFlagOn,
    workdayIntegration,
    isEnabled: isFlagOn && !!workdayIntegration,
  };
};

interface UseWorkdayPositionStatusArgs {
  applicationId?: string | number;
  /** `workday_position` straight off the application `show` response. */
  position?: WorkdayPosition | null;
  /** Gate from `useWorkdayPositionGate` — no calls when the flag is off. */
  enabled?: boolean;
  /** Called once the position reaches `ready`, to refetch the application. */
  onSynced?: () => void;
}

/**
 * Calls `position_status` exactly once — when the application opens on a
 * `syncing` position — and exposes `checkAgain` for the button. Stops entirely
 * once `ready`. No polling: the wait is 4–5 minutes at best and hours at worst.
 */
export const useWorkdayPositionStatus = ({
  applicationId,
  position,
  enabled = false,
  onSynced,
}: UseWorkdayPositionStatusArgs) => {
  const isSyncing = position?.status === 'syncing';
  const shouldCheck = !!(enabled && applicationId && isSyncing);

  const {
    data: checkedPosition,
    isFetching,
    error,
    refetch,
  } = useQuery({
    queryKey: WORKDAY_POSITION_QUERY_KEYS.status(applicationId ?? ''),
    queryFn: async () => {
      const response = await WORKDAY_POSITION_API.getPositionStatus(
        applicationId as string | number
      );
      return response?.data ?? null;
    },
    enabled: shouldCheck,
    // A sync that has not landed yet is not cacheable — "Check again" must ask
    // the server every time, however long the user has been waiting.
    staleTime: 0,
    gcTime: 0,
    refetchOnWindowFocus: false,
    retry: false,
  });

  // The status response is normally fresher than the payload that seeded it, so
  // it wins — except once the payload itself says `ready`. A held `syncing`
  // response would otherwise outrank an application refetched after someone
  // else's check moved it on, leaving the card and the menu disagreeing.
  const resolvedPosition: WorkdayPosition | null | undefined =
    position?.status === 'ready' ? position : (checkedPosition ?? position);

  const onSyncedRef = useRef(onSynced);
  onSyncedRef.current = onSynced;
  const notifiedRef = useRef(false);

  useEffect(() => {
    if (checkedPosition?.status !== 'ready') return;
    // The application refetch this triggers re-renders us with a `ready`
    // position; without the latch that would fire again on every render pass.
    if (notifiedRef.current) return;
    notifiedRef.current = true;
    onSyncedRef.current?.();
  }, [checkedPosition?.status]);

  const checkAgain = useCallback(() => {
    notifiedRef.current = false;
    return refetch();
  }, [refetch]);

  return {
    position: resolvedPosition,
    isChecking: isFetching,
    checkError: error,
    checkAgain,
  };
};
